/**
 * Control Room: arkivere en testorganisasjon (F58, runde 4. oktober; migrering 0722).
 *
 * «Arkiver, ikke slett.» Å arkivere fryser organisasjonen (ingen kommer inn) og tar den ut av lista;
 * filteret «Arkiverte» viser den igjen. Å ta den ut av arkivet åpner den ikke — den står fryst til
 * plattformadminen trykker «Åpne».
 *
 * `GET|PUT /v1/platform/organizations/:id/archive`. Bare for plattformadminer; alle andre får 404.
 */
import { z } from 'zod';

export const controlRoomOrganizationArchiveSchema = z
  .object({
    organizationId: z.uuid(),
    archived: z.boolean(),
    archivedAt: z.iso.datetime().nullable(),
    archivedReason: z.string().nullable(),
    archivedByEmail: z.string().nullable(),
  })
  .meta({ id: 'ControlRoomOrganizationArchive' });
export type ControlRoomOrganizationArchive = z.infer<typeof controlRoomOrganizationArchiveSchema>;

export const setControlRoomOrganizationArchivedRequestSchema = z
  .object({
    archived: z.boolean(),
    reason: z.string().trim().max(1000).optional(),
  })
  .meta({ id: 'SetControlRoomOrganizationArchivedRequest' });
export type SetControlRoomOrganizationArchivedRequest = z.infer<
  typeof setControlRoomOrganizationArchivedRequestSchema
>;

/*
 * ================================================================================================
 * SLETTING MED ANGREFRIST (QA13 #8, 253.65–253.67; migrering 0941).
 *
 *   GET    /v1/platform/organizations/:id/deletion   → ControlRoomOrganizationDeletion | 204-lignende null
 *   PUT    /v1/platform/organizations/:id/deletion   { confirmName } → planlagt, 30 dager
 *   DELETE /v1/platform/organizations/:id/deletion   → angret
 *   PUT    /v1/platform/people/:personId/deletion    { confirmEmail } → planlagt, 30 dager
 *   DELETE /v1/platform/people/:personId/deletion    → angret
 *   GET    /v1/platform/deletions                    → alt som står i «Planlagt sletting», og jobben
 *
 * En organisasjon kan bare planlegges slettet når den er arkivert. Jobben som utfører slettingen er
 * AV til Atle sier ja (`jobEnabled`), og ingenting i API-et kan slå den på.
 * ================================================================================================
 */

/** Detaljkodene en planlegging kan avvises med (VALIDATION_FAILED). */
export const CONTROL_ROOM_DELETION_REFUSAL_CODES = [
  'not_archived',
  'name_mismatch',
  'confirm_mismatch',
  'already_scheduled',
  'not_scheduled',
  'platform_organization',
  'platform_admin',
  'sole_superadmin',
  'self',
] as const;
export type ControlRoomDeletionRefusalCode = (typeof CONTROL_ROOM_DELETION_REFUSAL_CODES)[number];

export const controlRoomOrganizationDeletionSchema = z
  .object({
    organizationId: z.uuid(),
    /** Null: ikke planlagt. */
    scheduledAt: z.iso.datetime().nullable(),
    dueAt: z.iso.datetime().nullable(),
    scheduledByEmail: z.string().nullable(),
  })
  .meta({ id: 'ControlRoomOrganizationDeletion' });
export type ControlRoomOrganizationDeletion = z.infer<typeof controlRoomOrganizationDeletionSchema>;

export const scheduleOrganizationDeletionRequestSchema = z
  .object({ confirmName: z.string().max(300) })
  .meta({ id: 'ScheduleOrganizationDeletionRequest' });
export type ScheduleOrganizationDeletionRequest = z.infer<
  typeof scheduleOrganizationDeletionRequestSchema
>;

export const scheduleUserDeletionRequestSchema = z
  .object({ confirmEmail: z.string().max(320) })
  .meta({ id: 'ScheduleUserDeletionRequest' });
export type ScheduleUserDeletionRequest = z.infer<typeof scheduleUserDeletionRequestSchema>;

export const CONTROL_ROOM_DELETION_KINDS = ['organization', 'user'] as const;
export type ControlRoomDeletionKind = (typeof CONTROL_ROOM_DELETION_KINDS)[number];

export const controlRoomScheduledDeletionSchema = z
  .object({
    kind: z.enum(CONTROL_ROOM_DELETION_KINDS),
    /** Organisasjonens id, eller brukerens. */
    id: z.uuid(),
    /** Personen bak brukeren; null for en organisasjon. */
    personId: z.uuid().nullable(),
    name: z.string(),
    email: z.string().nullable(),
    scheduledAt: z.iso.datetime(),
    dueAt: z.iso.datetime(),
    scheduledByEmail: z.string().nullable(),
  })
  .meta({ id: 'ControlRoomScheduledDeletion' });
export type ControlRoomScheduledDeletion = z.infer<typeof controlRoomScheduledDeletionSchema>;

export const controlRoomScheduledDeletionListResponseSchema = z
  .object({
    items: z.array(controlRoomScheduledDeletionSchema),
    /** Jobben som sletter når fristen er ute. Av som standard (kontrakten for bølge C). */
    jobEnabled: z.boolean(),
    jobLastRunAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'ControlRoomScheduledDeletionListResponse' });
export type ControlRoomScheduledDeletionListResponse = z.infer<
  typeof controlRoomScheduledDeletionListResponseSchema
>;
